// src/components/Inventory/MaxBid.js
import React, { useContext, useMemo } from 'react';
import { View, Pressable } from 'react-native';
import { useMutation, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components/native';

import AuthContext from 'src/contexts/AuthContext';
import { Button } from 'src/elements';
import Input from 'src/elements/inputs';
import { Text } from 'src/styles';
import { dollars } from 'src/utils/stringHelpers';

import { CREATE_PRE_BID } from './queries';
import { SALE } from 'src/components/Bidding/queries';

export const Container = styled(View)`
  margin-top: 20px;
  padding: 10px;
  border-width: 2px;
  border-color: black;
  border-style: solid;
  border-radius: 4px;
`;

export const TitleContainer = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`;

export const Title = styled(Text)`
  font-size: 24px;
  margin-bottom: 12px;
  font-weight: 600;
`;

export const Warning = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: red;
`;

/**
 * Header rules:
 * - No Max Bid entered      => "Enter Max Bid"
 * - Max Bid surpassed       => "Enter New Max Bid"
 * - Max Bid not surpassed   => "Your Max Bid is $x.xx"
 */
const headerTextFor = (saleAsset) => {
  const myMax = saleAsset?.myMaxBidPrice;
  const surpassed = !!saleAsset?.myMaxBidSurpassed;

  if (!myMax) return 'Enter Max Bid';
  if (surpassed) return 'Enter New Max Bid';
  return `Your Max Bid is ${dollars(myMax)}`;
};

// Minimal fragment to update viewer fields locally
const SALE_ASSET_MAX_BID_FRAGMENT = gql`
  fragment SaleAssetMaxBidFields on SaleAsset {
    id
    __typename
    myMaxBidPrice
    myMaxBidSurpassed
  }
`;

const Content = ({ saleAsset, currentUser, setShow, saleId }) => {
  const asset = saleAsset.asset;

  const defaultPrice = useMemo(() => {
    if (saleAsset?.myMaxBidPrice) return String(saleAsset.myMaxBidPrice);
    if (saleAsset?.biddingNext) return String(saleAsset.biddingNext);
    if (saleAsset?.highBid) return String(parseInt(saleAsset.highBid, 10));
    return '';
  }, [saleAsset]);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      saleAssetId: saleAsset.id,
      type: 'Max',
      price: defaultPrice,
    }
  });

  const [createPreBid] = useMutation(CREATE_PRE_BID, {
    // Backstop refetch (authoritative), but local cache update is what makes it instant
    refetchQueries: saleId ? [{ query: SALE, variables: { id: saleId } }] : [],
    awaitRefetchQueries: false,

    update: (cache, { data }) => {
      // Regardless of server payload, we can safely reflect the user’s submitted max bid locally.
      // If server rejects, you’ll want to show errors (not shown here).
      const saCacheId = cache.identify({ __typename: 'SaleAsset', id: saleAsset.id });
      if (!saCacheId) return;

      // We already know what the user submitted (handled in onSubmit), but we can’t access it here directly.
      // So we rely on the mutation returning something OR we write in onSubmit (below).
      // This update is still useful if your server returns updated saleAsset fields in response.
      // If not, the onSubmit writeFragment (below) will handle it.
    },

    onCompleted: () => {
      if (setShow) setShow(false);
    },
  });

  const onSubmit = (input) => {
    const priceInt = parseInt(input.price, 10);
    const confirmation = `Confirm Max Bid of ${dollars(priceInt)} for ${asset.name}`;
    if (!window.confirm(confirmation)) return;

    // ✅ Immediate UI update: write viewer max-bid fields to the SaleAsset in cache
    // This fixes “nothing changes until refresh” for max bid.
    // (This is viewer-specific state, so the subscription usually won’t provide it.)
    try {
      // We don’t have cache here unless we use the client, so we do the write in optimistic update form:
      // easiest: use the mutation’s update via a function param? Instead, we use "optimisticResponse" and "update".
      // But schema might not match. So we do a safe write via Apollo cache by using the mutation function's "update" argument.
    } catch (e) {
      // no-op
    }

    createPreBid({
      variables: {
        saleAssetId: input.saleAssetId,
        type: 'Max',
        price: priceInt,
      },
      // ✅ Do the cache write here (we have access to cache through the "update" option at call-time)
      update: (cache) => {
        const saCacheId = cache.identify({ __typename: 'SaleAsset', id: saleAsset.id });
        if (!saCacheId) return;

        cache.writeFragment({
          id: saCacheId,
          fragment: SALE_ASSET_MAX_BID_FRAGMENT,
          data: {
            __typename: 'SaleAsset',
            id: saleAsset.id,
            myMaxBidPrice: String(priceInt),
            myMaxBidSurpassed: false,
          },
        });
      },
    });
  };

  if (!currentUser?.buyer) {
    return (
      <Link to="/login">
        <Warning>
          Click here to log in or register in order to bid.
        </Warning>
      </Link>
    );
  } else if ('Review' === currentUser.buyer.status) {
    return (
      <Warning>
        Your account is currently being reviewed for approval to bid.
      </Warning>
    );
  } else if ('New' === currentUser.buyer.status) {
    return (
      <>
        <Warning>
          You must provide billing information before we can review
          your account.
        </Warning>
        <Link to="/account/billing">
          <Warning>
            Click Here to finish setting up your account.
          </Warning>
        </Link>
      </>
    );
  } else if (currentUser.buyer.isApproved) {
    return (
      <View>
        <View style={{ flexDirection: 'row', gap: 30, marginTop: 20 }}>
          <Input.Currency
            label="Your Max Bid:"
            name="price"
            control={control}
            errors={errors}
            rules={{ required: true }}
          />
          <Button
            onClick={handleSubmit(onSubmit)}
            style={{ maxWidth: 200 }}
          >
            Submit Bid
          </Button>
        </View>
      </View>
    );
  }

  return null;
};

const MaxBid = ({ saleAsset, setShow, saleId }) => {
  const { currentUser } = useContext(AuthContext);

  return (
    <Container>
      <TitleContainer>
        <Title>{headerTextFor(saleAsset)}</Title>
        <Pressable onPress={() => setShow(false)}>
          <Title>X</Title>
        </Pressable>
      </TitleContainer>

      <Content
        saleAsset={saleAsset}
        currentUser={currentUser}
        setShow={setShow}
        saleId={saleId}
      />
    </Container>
  );
};

export default MaxBid;
